import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, NavLink } from "react-router-dom";
import { useSnackbar } from "notistack";
import axios from "../../api/axios";
import styles from "../ActivateAccount/ActivateAccount.module.css";
import { Typography, CircularProgress } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

export default function ConfirmEmailChange() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("processing"); // 'processing' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState("");
  const [newEmail, setNewEmail] = useState("");

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMessage("Invalid confirmation link. Missing token.");
      return;
    }

    const confirm = async () => {
      try {
        const response = await axios.post("/api/users/change-email/confirm", { token });
        if (response.data.success) {
          setNewEmail(response.data.email || "");
          setStatus("success");
          enqueueSnackbar("Your email address has been updated.", {
            variant: "success",
            autoHideDuration: 6000,
          });
          setTimeout(() => navigate("/login"), 4000);
        } else {
          throw new Error(response.data.error || "Confirmation failed");
        }
      } catch (error) {
        const msg =
          error.response?.data?.error ||
          error.message ||
          "We couldn't confirm your email change. The link may have expired.";
        setStatus("error");
        setErrorMessage(msg);
        enqueueSnackbar(msg, { variant: "error", autoHideDuration: 8000 });
      }
    };

    confirm();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className={styles.accountNav}>
        <Typography className={styles.navText}>
          Sharing God's Love in Word and Deed
        </Typography>
      </div>
      <div className={styles.ActivateAccountMain}>
        <div id="app" className={styles.contentHalf}>
          <section className={styles.activationSection}>
            <img
              src="./Images/baptists-on-mission-logo.png"
              alt="Baptists on Mission Logo"
              className={styles.bomLogo}
            />

            {/* Processing */}
            {status === "processing" && (
              <div className={styles.loadingContainer}>
                <CircularProgress size={48} sx={{ color: "#003063" }} />
                <Typography variant="h6" className={styles.loadingText} sx={{ mt: 2 }}>
                  Confirming your new email address…
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Please wait, this only takes a moment.
                </Typography>
              </div>
            )}

            {/* Success */}
            {status === "success" && (
              <div className={styles.successContainer}>
                <div className={styles.successMessage}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "#2e7d32", mb: 1 }} />
                  <Typography variant="h4" className={styles.successTitle}>
                    Email Updated!
                  </Typography>
                  <Typography variant="body1" className={styles.successText} sx={{ mt: 1 }}>
                    {newEmail
                      ? `Your email address is now ${newEmail}. Please log in with your new email.`
                      : "Your email address has been updated. Please log in with your new email."}
                  </Typography>
                  <Typography variant="body2" className={styles.redirectText} sx={{ mt: 1 }}>
                    Redirecting you to sign in…
                  </Typography>
                  <Typography variant="caption" className={styles.manualRedirect} sx={{ mt: 1, display: "block" }}>
                    <NavLink to="/login">Click here to sign in now</NavLink>
                  </Typography>
                </div>
              </div>
            )}

            {/* Error */}
            {status === "error" && (
              <div className={styles.errorContainer}>
                <div className={styles.errorMessage}>
                  <ErrorOutlineIcon sx={{ fontSize: 48, color: "#c62828", mb: 1 }} />
                  <Typography variant="h4" className={styles.errorTitle}>
                    Confirmation Failed
                  </Typography>
                  <Typography variant="body1" className={styles.errorText} sx={{ mt: 1 }}>
                    {errorMessage}
                  </Typography>
                  <div className={styles.errorActions} style={{ marginTop: 16 }}>
                    <Typography variant="body2" className={styles.helpText}>
                      You can request the change again from your account Settings.
                    </Typography>
                    <Typography variant="caption" className={styles.loginLink} sx={{ display: "block", mt: 1 }}>
                      <NavLink to="/login">Back to Sign In</NavLink>
                    </Typography>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
        <img
          className={styles.activateAccountImg}
          src="./Images/create-account-page-image.jpg"
          alt="Baptists on Mission confirm email"
        />
      </div>
    </>
  );
}
